import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, ArrowRight, Loader2, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { referralsApi } from "@/lib/apiClient";
import { toast } from "sonner";

const REF_KEY = "learnos_ref";

export default function Register() {
    const { register } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [refCode, setRefCode] = useState("");
    const [refName, setRefName] = useState("");

    // Capture a referral code from the URL (?ref=CODE) or a previously stored one.
    useEffect(() => {
        let code = (searchParams.get("ref") || "").trim().toUpperCase();
        if (!code) {
            try { code = (localStorage.getItem(REF_KEY) || "").trim().toUpperCase(); } catch (e) { /* */ }
        }
        if (!code) return;
        setRefCode(code);
        try { localStorage.setItem(REF_KEY, code); } catch (e) { /* */ }
        (async () => {
            try {
                const res = await referralsApi.validateCode(code);
                if (res.valid) setRefName(res.ambassador_name);
                else setRefCode("");
            } catch (e) { /* */ }
        })();
    }, [searchParams]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            toast.error(t("auth.register.pwShort"));
            return;
        }
        setLoading(true);
        try {
            await register(name.trim(), email.trim(), password, refCode || undefined);
            try { localStorage.removeItem(REF_KEY); } catch (er) { /* */ }
            toast.success(t("auth.register.created"));
            navigate("/library");
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("auth.register.failed"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-screen items-center justify-center bg-surface-0 px-4">
            <div className="hero-wash pointer-events-none absolute inset-0" />
            <div className="absolute right-4 top-4 z-10"><LanguageSwitcher testId="language-switcher-auth" /></div>
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-md rounded-3xl border border-border bg-surface-1 p-7 shadow-elev-1 sm:p-8"
            >
                <Link to="/" className="mb-6 inline-flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                        <GraduationCap className="h-5 w-5" />
                    </div>
                    <span className="font-sans-display text-base font-semibold text-t1">LearnOS</span>
                </Link>
                <h1 className="font-sans-display text-2xl font-semibold tracking-tight text-t1">{t("auth.register.title")}</h1>
                <p className="mt-1.5 text-sm text-t3">{t("auth.register.subtitle")}</p>

                {refName && (
                    <div data-testid="register-ref-notice" className="mt-4 flex items-center gap-2.5 rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 px-3.5 py-2.5">
                        <Gift className="h-4 w-4 shrink-0 text-[hsl(var(--primary))]" />
                        <span className="text-sm text-t2">{t("auth.register.invitedBy", { name: refName })}</span>
                    </div>
                )}

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="name" className="text-t2">{t("auth.register.name")}</Label>
                        <Input data-testid="register-name-input" id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.register.namePlaceholder")} className="bg-surface-2" />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="email" className="text-t2">{t("auth.register.email")}</Label>
                        <Input data-testid="register-email-input" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.register.emailPlaceholder")} className="bg-surface-2" />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="password" className="text-t2">{t("auth.register.password")}</Label>
                        <Input data-testid="register-password-input" id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.register.passwordPlaceholder")} className="bg-surface-2" />
                    </div>
                    <Button data-testid="register-submit-button" type="submit" disabled={loading} className="w-full gap-2">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("auth.register.submit")} <ArrowRight className="h-4 w-4" /></>}
                    </Button>
                </form>

                <p className="mt-6 text-center text-sm text-t3">
                    {t("auth.register.haveAccount")}{" "}
                    <Link to="/login" className="font-medium text-[hsl(var(--primary))] hover:underline">{t("auth.register.signIn")}</Link>
                </p>
            </motion.div>
        </div>
    );
}
